@let isMobileSearch = device.isMobileUI();
@let isModalOpen = modalOpen();
@let searchQuery = query();
@let items = menuItems();

@if (isMobileSearch) {
  <button
    mlButton
    variant="ghost"
    intent="neutral"
    size="sm"
    userClass="header-round-action"
    [attr.aria-label]="'header.searchAria' | mlTranslate"
    [attr.aria-expanded]="isModalOpen"
    (click)="onOpenModal()"
  >
    <ml-icon name="search" size="sm" />
  </button>

  <ion-modal
    data-header-page-modal
    [isOpen]="isModalOpen"
    (didPresent)="onSearchPresented($event)"
    (didDismiss)="onModalDismiss()"
  >
    <ng-template>
      <div class="header-page-modal" [class.platform-ios]="isIos()">
        <div class="border-splash flex items-center gap-2 border-b px-4 py-3">
          <button
            type="button"
            class="inline-flex size-8 shrink-0 items-center justify-center text-neutral-900"
            [attr.aria-label]="'header.searchBackAria' | mlTranslate"
            (click)="closeModal()"
          >
            <ml-icon [name]="'arrowLeft'" aria-hidden="true" />
          </button>
          <div class="min-w-0 flex-1">
            <ml-textfield
              type="search"
              inputMode="search"
              leadingIcon="search"
              autocomplete="off"
              [maxLength]="searchMaxLength"
              [placeholder]="'header.searchPlaceholder' | mlTranslate"
              [trailingIcon]="searchQuery ? 'closeCircleFilled' : null"
              [trailingIconInteractive]="true"
              [trailingIconAriaLabel]="'header.search.clear' | mlTranslate"
              [value]="searchQuery"
              (valueChange)="onQueryChange($event)"
              (endIconClick)="onQueryChange('')"
            />
          </div>
        </div>

        <div class="header-search-list bg-neutral-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
          <ng-container [ngTemplateOutlet]="searchStatus" />

          @for (row of buildRows(items); track row.id) {
            @switch (row.kind) {
              @case ('separator') {}
              @case ('title') {
                <div role="presentation" [class]="titleClass">
                  <span class="min-w-0 truncate">
                    {{ row.titleKey | mlTranslate: row.titleParams }}
                  </span>
                  @if (row.actionLabelKey && row.actionValue) {
                    <button
                      mlButton
                      variant="link"
                      userClass="text-body-small ml-auto h-auto gap-0.5 px-0 font-normal normal-case"
                      [suffixIconName]="row.actionIcon ?? null"
                      suffixIconSize="sm"
                      (click)="onSelect(row.actionValue)"
                    >
                      {{ row.actionLabelKey | mlTranslate: row.actionLabelParams }}
                    </button>
                  }
                </div>
              }
              @default {
                <button
                  type="button"
                  [disabled]="row.item.disabled ?? false"
                  [class]="itemClass({ destructive: row.item.destructive ?? false })"
                  (click)="onSelect(row.item.value)"
                >
                  @if (row.item.icon; as icon) {
                    <ml-icon [name]="icon" class="text-secondary" />
                  }
                  <span class="flex min-w-0 flex-auto flex-col">
                    <span class="truncate" [class.font-semibold]="row.item.descriptionKey || row.item.description">
                      {{ row.item.labelKey || row.item.label | mlTranslate }}
                    </span>
                    @if (row.item.descriptionKey || row.item.description) {
                      <span class="text-caption text-neutral-300">
                        {{ row.item.descriptionKey || row.item.description | mlTranslate }}
                      </span>
                    }
                  </span>
                </button>
              }
            }
          }
        </div>
      </div>
    </ng-template>
  </ion-modal>
} @else {
  <div class="w-full" [mlMenuTrigger]="searchMenu" mlMenuTriggerPlacement="bottom-start">
    <ml-textfield
      type="search"
      inputMode="search"
      leadingIcon="search"
      autocomplete="off"
      [maxLength]="searchMaxLength"
      [placeholder]="'header.searchPlaceholder' | mlTranslate"
      [trailingIcon]="searchQuery ? 'closeCircleFilled' : null"
      [trailingIconInteractive]="true"
      [trailingIconAriaLabel]="'header.search.clear' | mlTranslate"
      [value]="searchQuery"
      (valueChange)="onQueryChange($event)"
      (endIconClick)="onQueryChange('')"
    />
  </div>

  <ml-menu
    #searchMenu
    retainTriggerFocus
    [panelClass]="searchMenuPanelClass()"
    [items]="items"
    [sheetOnMobile]="false"
    (itemSelect)="onSelect($event)"
  >
    <ng-container [ngTemplateOutlet]="searchStatus" />
  </ml-menu>
}

<ng-template #searchStatus>
  @if (shellStore.loading()) {
    <div role="status" [attr.aria-label]="'header.search.searching' | mlTranslate">
      <div role="presentation" [class]="titleClass">
        {{ 'header.search.searching' | mlTranslate }}
      </div>
      @for (row of skeletonRows; track row) {
        <div class="flex h-13.25 items-center gap-2.5 px-4">
          <ml-skeleton class="size-8 shrink-0" radius="md" />
          <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <ml-skeleton class="h-4 w-3/4" />
            <ml-skeleton class="h-4 w-1/2" />
          </div>
        </div>
      }
    </div>
  } @else if (showEmpty()) {
    <div class="flex min-h-full flex-1 flex-col items-center justify-center px-6 py-8 text-center" role="status">
      <img src="assets/icon/empty-search.svg" alt="" [class]="isMobileSearch ? 'size-67.75' : 'h-26 w-29.5'" />
      <p class="text-body mt-4 font-semibold text-neutral-900">
        {{ 'header.search.emptyTitle' | mlTranslate }}
      </p>
      <p class="text-body-small mt-1 text-neutral-300">
        {{ 'header.search.emptyDescription' | mlTranslate }}
      </p>
    </div>
  }
</ng-template>
